"use client";

import { c as _c } from "react/compiler-runtime";
import { memo, useEffect, useMemo, useState } from "react";
import type React from "react";
import { convertCents, formatMoney, normalizeCurrency, type Currency } from "@lib/currency";
import { STORE_BASE_CURRENCY } from "@lib/config";
import { fetchRates, getCachedRates } from "@lib/forex-client";
const readCookie = (name: string): string | null => {
  if (typeof document === "undefined") return null;
  const entries = document.cookie.split(";");
  for (const entry of entries) {
    const trimmed = entry.trim();
    if (!trimmed.startsWith(`${name}=`)) continue;
    return decodeURIComponent(trimmed.slice(name.length + 1));
  }
  return null;
};
const readLocalCurrency = (): string | null => {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem("lealti_currency");
  } catch {
    return null;
  }
};
type MoneyClientProps = React.HTMLAttributes<HTMLSpanElement> & {
  amountCents: number;
  fromCurrency?: Currency;
  showBaseWhileLoading?: boolean;
  lockCurrency?: boolean;
};
function MoneyClient(t0) {
  const $ = _c(37);
  let amountCents;
  let className;
  let fromCurrency;
  let rest;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      amountCents,
      fromCurrency,
      showBaseWhileLoading: t1,
      lockCurrency: t2,
      className,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = amountCents;
    $[2] = className;
    $[3] = fromCurrency;
    $[4] = rest;
    $[5] = t1;
    $[6] = t2;
  } else {
    amountCents = $[1];
    className = $[2];
    fromCurrency = $[3];
    rest = $[4];
    t1 = $[5];
    t2 = $[6];
  }
  const showBaseWhileLoading = t1 === undefined ? true : t1;
  const lockCurrency = t2 === undefined ? false : t2;
  let t3;
  if ($[7] !== fromCurrency) {
    t3 = normalizeCurrency(fromCurrency, STORE_BASE_CURRENCY);
    $[7] = fromCurrency;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const baseCurrency = t3;
  const [selectedCurrency, setSelectedCurrency] = useState(baseCurrency);
  const [rates, setRates] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  let t4;
  let t5;
  if ($[9] !== baseCurrency) {
    t4 = () => {
      const resolved = normalizeCurrency(readCookie("lealti_currency") || readLocalCurrency(), baseCurrency);
      setSelectedCurrency(resolved);
    };
    t5 = [baseCurrency];
    $[9] = baseCurrency;
    $[10] = t4;
    $[11] = t5;
  } else {
    t4 = $[10];
    t5 = $[11];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[12] !== baseCurrency) {
    t6 = () => {
      const handler = event => {
        const detail = (event as CustomEvent<{
          currency?: string;
        }>).detail;
        if (detail?.currency) {
          setSelectedCurrency(normalizeCurrency(detail.currency, baseCurrency));
        }
      };
      if (typeof window !== "undefined") {
        window.addEventListener("lealti:currency", handler as EventListener);
        return () => window.removeEventListener("lealti:currency", handler as EventListener);
      }
    };
    t7 = [baseCurrency];
    $[12] = baseCurrency;
    $[13] = t6;
    $[14] = t7;
  } else {
    t6 = $[13];
    t7 = $[14];
  }
  useEffect(t6, t7);
  let t8;
  let t9;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      let active = true;
      const cached = getCachedRates();
      if (cached) {
        setRates(cached);
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      fetchRates().then(snapshot => {
        if (!active) {
          return;
        }
        if (snapshot) {
          setRates(snapshot);
        }
        setIsLoading(false);
      });
      return () => {
        active = false;
      };
    };
    t9 = [];
    $[15] = t8;
    $[16] = t9;
  } else {
    t8 = $[15];
    t9 = $[16];
  }
  useEffect(t8, t9);
  let t10;
  bb0: {
    const effectiveCurrency = lockCurrency ? baseCurrency : selectedCurrency;
    if (!rates || lockCurrency || effectiveCurrency === baseCurrency) {
      let t11;
      if ($[17] !== amountCents || $[18] !== baseCurrency) {
        t11 = formatMoney(amountCents, baseCurrency);
        $[17] = amountCents;
        $[18] = baseCurrency;
        $[19] = t11;
      } else {
        t11 = $[19];
      }
      t10 = t11;
      break bb0;
    }
    let t11;
    if ($[20] !== amountCents || $[21] !== baseCurrency || $[22] !== effectiveCurrency || $[23] !== rates.base || $[24] !== rates.rates) {
      const converted = convertCents({
        amountCents,
        fromCurrency: baseCurrency,
        toCurrency: effectiveCurrency,
        rates: rates.rates,
        base: rates.base
      });
      t11 = formatMoney(converted, effectiveCurrency);
      $[20] = amountCents;
      $[21] = baseCurrency;
      $[22] = effectiveCurrency;
      $[23] = rates.base;
      $[24] = rates.rates;
      $[25] = t11;
    } else {
      t11 = $[25];
    }
    t10 = t11;
  }
  const formatted = t10;
  if (showBaseWhileLoading && isLoading && !lockCurrency) {
    const t11 = "font-lealti-light  " + (className || "");
    let t12;
    if ($[26] !== amountCents || $[27] !== baseCurrency) {
      t12 = formatMoney(amountCents, baseCurrency);
      $[26] = amountCents;
      $[27] = baseCurrency;
      $[28] = t12;
    } else {
      t12 = $[28];
    }
    let t13;
    if ($[29] !== rest || $[30] !== t11 || $[31] !== t12) {
      t13 = <span className={t11} {...rest}>{t12}</span>;
      $[29] = rest;
      $[30] = t11;
      $[31] = t12;
      $[32] = t13;
    } else {
      t13 = $[32];
    }
    return t13;
  }
  const t11 = "font-lealti-light  " + (className || "");
  let t12;
  if ($[33] !== formatted || $[34] !== rest || $[35] !== t11) {
    t12 = <span className={t11} {...rest}>{formatted}</span>;
    $[33] = formatted;
    $[34] = rest;
    $[35] = t11;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  return t12;
}
export default memo(MoneyClient);